import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ScrollText, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { useAuth } from "@/auth/AuthContext";
import ConfirmationGate, { useConfirmationGate } from "@/components/ConfirmationGate";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
  { value: "PETS", label: "Pets" },
  { value: "PARKING", label: "Parking" },
  { value: "SHORT_STAY", label: "Short-stay" },
  { value: "COMMON_PROPERTY", label: "Common property" },
  { value: "NOISE", label: "Noise" },
  { value: "OTHER", label: "Other" },
];

export default function BylawNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const schemeId = getActiveSchemeId();
  const gate = useConfirmationGate({
    onConfirmed: () => navigate("/scheme/bylaws"),
  });
  const [meetings, setMeetings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    by_law_number: "",
    title: "",
    full_text: "",
    category: "OTHER",
    status: "PROPOSED",
    effective_date: "",
    special_resolution_meeting_id: "__none__",
    document_id: "__none__",
  });

  useEffect(() => {
    let mounted = true;
    if (!schemeId) {
      navigate("/scheme/bylaws");
      return;
    }
    (async () => {
      try {
        const [{ data: mtgs }, { data: docs }, { data: mems }] = await Promise.all([
          api.get(`/schemes/${schemeId}/meetings`),
          api.get(`/schemes/${schemeId}/documents`),
          api.get(`/schemes/${schemeId}/members`),
        ]);
        if (!mounted) return;
        setMeetings(mtgs);
        setDocuments(docs);
        setMembers(mems);
      } catch (e) {
        if (mounted) setError(apiError(e));
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const myMembership = members.find((mm) => mm.user_id === user?.user_id);
  const canWrite =
    ["chair", "committee"].includes(myMembership?.role) ||
    user?.system_role === "platform_admin";

  // Gate: redirect non-writers (defensive — App.js already wraps with NonSP)
  useEffect(() => {
    if (members.length > 0 && !canWrite) {
      navigate("/scheme/bylaws", { replace: true });
    }
  }, [members, canWrite, navigate]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.by_law_number.trim() || !form.title.trim() || !form.full_text.trim()) {
      toast.error("By-law number, title, and full text are required.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        by_law_number: form.by_law_number.trim(),
        title: form.title.trim(),
        full_text: form.full_text,
        category: form.category,
        status: form.status,
      };
      if (form.effective_date) body.effective_date = form.effective_date;
      if (form.special_resolution_meeting_id && form.special_resolution_meeting_id !== "__none__")
        body.special_resolution_meeting_id = form.special_resolution_meeting_id;
      if (form.document_id && form.document_id !== "__none__")
        body.document_id = form.document_id;
      const result = await gate.fire("post", `/schemes/${schemeId}/bylaws`, body, { __scheme_id: schemeId });
      if (result) {
        toast.success(`By-law #${result.by_law_number} created`);
        navigate(`/scheme/bylaws/${result.bylaw_id}`);
      }
      // If null: modal opened; onConfirmed callback (below) navigates after confirm.
    } catch (err) {
      toast.error(apiError(err));
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-8" data-testid="bylaw-new-page">
        <Link to="/scheme/bylaws">
          <Button variant="ghost" size="sm" className="h-8 mb-4 -ml-2" data-testid="bylaw-new-back">
            <ArrowLeft size={14} className="mr-1.5" />
            Back to by-laws
          </Button>
        </Link>

        <div className="mb-6">
          <h1
            className="text-2xl font-bold tracking-tight flex items-center gap-2"
            style={{ color: "var(--navy)" }}
            data-testid="bylaw-new-heading"
          >
            <ScrollText size={22} style={{ color: "var(--blue)" }} />
            New by-law
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>
            Draft a by-law. PROPOSED is the recommended default — flip to ACTIVE after the
            scheme adopts it via special resolution.
          </p>
        </div>

        {error && (
          <div
            className="rounded-lg p-4 text-sm mb-6 flex items-center gap-2"
            style={{ background: "var(--alert-light)", color: "var(--alert)" }}
            data-testid="bylaw-new-error"
          >
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        <form
          onSubmit={onSubmit}
          className="rounded-xl bg-white border p-5 space-y-4"
          style={{ borderColor: "var(--border)" }}
          data-testid="bylaw-new-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="by_law_number" className="text-xs font-semibold mb-1.5 block">
                By-law number *
              </Label>
              <Input
                id="by_law_number"
                value={form.by_law_number}
                onChange={onChange("by_law_number")}
                placeholder="1, 2.1, Annex A"
                data-testid="bylaw-new-number"
                required
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="title" className="text-xs font-semibold mb-1.5 block">
                Title *
              </Label>
              <Input
                id="title"
                value={form.title}
                onChange={onChange("title")}
                placeholder="e.g. Pets, Parking, Short-stay accommodation"
                data-testid="bylaw-new-title"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="category" className="text-xs font-semibold mb-1.5 block">
                Category *
              </Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger id="category" data-testid="bylaw-new-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value} data-testid={`bylaw-new-category-${c.value}`}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="status" className="text-xs font-semibold mb-1.5 block">
                Status *
              </Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger id="status" data-testid="bylaw-new-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PROPOSED">Proposed</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="effective_date" className="text-xs font-semibold mb-1.5 block">
                Effective date
              </Label>
              <Input
                id="effective_date"
                type="date"
                value={form.effective_date}
                onChange={onChange("effective_date")}
                data-testid="bylaw-new-effective-date"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="full_text" className="text-xs font-semibold mb-1.5 block">
              Full text * <span className="font-normal text-slate-500">(markdown supported)</span>
            </Label>
            <Textarea
              id="full_text"
              rows={10}
              value={form.full_text}
              onChange={onChange("full_text")}
              placeholder="Owners and occupiers must obtain prior written consent from the committee before keeping any pet..."
              data-testid="bylaw-new-text"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="meeting" className="text-xs font-semibold mb-1.5 block">
                Linked meeting <span className="font-normal text-slate-500">(special resolution)</span>
              </Label>
              <Select
                value={form.special_resolution_meeting_id}
                onValueChange={(v) => setForm({ ...form, special_resolution_meeting_id: v })}
              >
                <SelectTrigger id="meeting" data-testid="bylaw-new-meeting"><SelectValue placeholder="(none)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">(none)</SelectItem>
                  {meetings.map((m) => (
                    <SelectItem key={m.meeting_id} value={m.meeting_id}>
                      {m.meeting_type} &mdash; {m.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="doc" className="text-xs font-semibold mb-1.5 block">
                Linked document
              </Label>
              <Select
                value={form.document_id}
                onValueChange={(v) => setForm({ ...form, document_id: v })}
              >
                <SelectTrigger id="doc" data-testid="bylaw-new-document"><SelectValue placeholder="(none)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">(none)</SelectItem>
                  {documents.map((d) => (
                    <SelectItem key={d.document_id} value={d.document_id}>
                      {d.filename}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="pt-2 flex items-center gap-2">
            <Button
              type="submit"
              disabled={saving}
              style={{ background: "var(--blue)", color: "white" }}
              data-testid="bylaw-new-submit"
            >
              {saving ? "Creating\u2026" : "Create by-law"}
            </Button>
            <Link to="/scheme/bylaws">
              <Button type="button" variant="ghost" data-testid="bylaw-new-cancel">Cancel</Button>
            </Link>
          </div>
        </form>
        <ConfirmationGate {...gate.gateProps} />
      </div>
    </AppShell>
  );
}
